import React, { useEffect, useRef, useState, useCallback } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';
import { Play, Pause, SkipBack, SkipForward, Repeat, Gauge, MapPin, X } from 'lucide-react';

export default function AudioPlayer({ audioUrl, versionId }) {
  // Detect iOS - MediaElement backend doesn't support regions well
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  
  const waveformRef = useRef(null);
  const wavesurferRef = useRef(null);
  const regionsPluginRef = useRef(null);
  const isMountedRef = useRef(true);
  
  // Use refs for values accessed in WaveSurfer callbacks
  const isLoopingRef = useRef(false);
  const loopRegionRef = useRef(null);
  const durationRef = useRef(0);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [isLooping, setIsLooping] = useState(false);
  const [loopRegion, setLoopRegion] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [showAddMarker, setShowAddMarker] = useState(false);
  const [newMarkerLabel, setNewMarkerLabel] = useState('');
  const [isReady, setIsReady] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Sync refs with state
  useEffect(() => {
    isLoopingRef.current = isLooping;
  }, [isLooping]);

  useEffect(() => {
    loopRegionRef.current = loopRegion;
  }, [loopRegion]);

  useEffect(() => {
    durationRef.current = duration;
  }, [duration]);

  // Initialize WaveSurfer ONCE on mount (no dependency on audioUrl)
  useEffect(() => {
    if (!waveformRef.current) return;

    console.log('[AudioPlayer] Initializing WaveSurfer');
    isMountedRef.current = true;

    // Create regions plugin
    const regions = RegionsPlugin.create();
    regionsPluginRef.current = regions;

    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    // Initialize WaveSurfer
    const wavesurfer = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: 'rgba(255, 255, 255, 0.5)',
      progressColor: '#8146FF',
      cursorColor: '#8146FF',
      barWidth: 2,
      barGap: 1,
      barRadius: 2,
      height: 80,
      normalize: true,
      plugins: [regions],
      backend: isIOS ? 'MediaElement' : 'WebAudio', // Use MediaElement on iOS
    });

    console.log('[AudioPlayer] Using backend:', isIOS ? 'MediaElement (iOS)' : 'WebAudio');

    wavesurferRef.current = wavesurfer;

    // Event listeners
    wavesurfer.on('ready', () => {
      if (isMountedRef.current) {
        setDuration(wavesurfer.getDuration());
        setIsReady(true);
        setLoadError(false);
      }
    });

    wavesurfer.on('audioprocess', () => {
      if (isMountedRef.current) {
        setCurrentTime(wavesurfer.getCurrentTime());
      }
    });

    wavesurfer.on('play', () => {
      if (isMountedRef.current) {
        setIsPlaying(true);
      }
    });

    wavesurfer.on('pause', () => {
      if (isMountedRef.current) {
        setIsPlaying(false);
      }
    });

    wavesurfer.on('finish', () => {
      if (isMountedRef.current) {
        setIsPlaying(false);
        if (isLoopingRef.current && loopRegionRef.current) {
          wavesurfer.seekTo(loopRegionRef.current.start / durationRef.current);
          wavesurfer.play();
        }
      }
    });

    wavesurfer.on('error', (error) => {
      // Only ignore AbortError specifically (happens when switching tracks)
      const isAbortError = error?.name === 'AbortError' || error?.message?.includes('aborted without reason');
      
      if (isAbortError) {
        console.log('[AudioPlayer] Load aborted (normal during track switching)');
        return;
      }
      
      // Log all other errors as they might be real issues
      console.error('[AudioPlayer] WaveSurfer error:', error);
      if (isMountedRef.current) {
        setIsReady(false);
        setLoadError(true);
      }
    });

    // Region events for A/B looping
    regions.on('region-out', (region) => {
      if (isMountedRef.current && isLoopingRef.current && region.id === 'loop-region') {
        wavesurfer.seekTo(region.start / durationRef.current);
        wavesurfer.play();
      }
    });

    // Cleanup ONLY on unmount
    return () => {
      console.log('[AudioPlayer] Component unmounting, cleaning up');
      isMountedRef.current = false;
      
      if (wavesurferRef.current) {
        const ws = wavesurferRef.current;
        wavesurferRef.current = null;
        regionsPluginRef.current = null;
        
        // Stop playback first
        try {
          if (ws.isPlaying && ws.isPlaying()) {
            ws.pause();
          }
        } catch (e) {
          // Ignore - instance might be destroyed
        }
        
        // Destroy immediately (empty() is called internally)
        try {
          ws.destroy();
        } catch (error) {
          // Silently ignore abort errors during cleanup
          if (!error?.message?.includes('aborted')) {
            console.warn('[AudioPlayer] Cleanup warning:', error);
          }
        }
      }
      
      setIsReady(false);
      setIsPlaying(false);
      setCurrentTime(0);
      setDuration(0);
      setLoopRegion(null);
      setIsLooping(false);
    };
  }, []); // Empty deps - only run on mount/unmount

  // Separate effect to load audio when URL changes
  useEffect(() => {
    const ws = wavesurferRef.current;
    if (!ws || !audioUrl) return;

    console.log('[AudioPlayer] Loading new audio:', audioUrl);
    setIsReady(false);
    setLoadError(false);
    setLoopRegion(null);
    setIsLooping(false);
    setMarkers([]);
    
    // Clear regions
    const regions = regionsPluginRef.current;
    if (regions) {
      try {
        regions.clearRegions();
      } catch (e) {
        // Ignore
      }
    }
    
    // Empty current waveform before loading new one
    try {
      ws.empty();
    } catch (e) {
      // Ignore if already empty
    }
    
    // Small delay to let empty() complete, then load
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current && wavesurferRef.current) {
        ws.load(audioUrl);
      }
    }, 10);
    
    // Cleanup timeout if component unmounts or audioUrl changes
    return () => clearTimeout(timeoutId);
  }, [audioUrl]);

  const togglePlayPause = useCallback(() => {
    const ws = wavesurferRef.current;
    if (!ws) return;
    
    // iOS Safari/Chrome require explicit AudioContext resume
    if (ws.backend?.ac) {
      const audioContext = ws.backend.ac;
      
      if (audioContext.state === 'suspended') {
        console.log('[AudioPlayer] Resuming suspended AudioContext (iOS fix)');
        audioContext.resume().then(() => {
          console.log('[AudioPlayer] AudioContext resumed, playing...');
          ws.playPause();
        }).catch(err => {
          console.error('[AudioPlayer] Failed to resume AudioContext:', err);
        });
      } else {
        ws.playPause();
      }
    } else {
      ws.playPause();
    }
  }, []);

  const skipBackward = () => {
    const ws = wavesurferRef.current;
    if (!ws) return;
    ws.skip(-5);
  };

  const skipForward = () => {
    const ws = wavesurferRef.current;
    if (!ws) return;
    ws.skip(5);
  };

  const changePlaybackRate = (rate) => {
    const ws = wavesurferRef.current;
    if (!ws) return;
    ws.setPlaybackRate(rate);
    setPlaybackRate(rate);
  };

  const setABLoop = () => {
    const ws = wavesurferRef.current;
    const regions = regionsPluginRef.current;
    if (!ws || !regions) return;

    // Clear existing loop region
    regions.clearRegions();
    setLoopRegion(null);

    // Create new loop region (default: 10% to 30% of track)
    const start = currentTime;
    const end = Math.min(start + 10, duration); // 10 second loop or to end

    regions.addRegion({
      id: 'loop-region',
      start: start,
      end: end,
      color: 'rgba(129, 70, 255, 0.3)',
      drag: true,
      resize: true,
    });

    setLoopRegion({ start, end });
    setIsLooping(true);
  };

  const clearLoop = useCallback(() => {
    const regions = regionsPluginRef.current;
    if (!regions) return;
    
    regions.clearRegions();
    setLoopRegion(null);
    setIsLooping(false);
    
    // Restore marker regions
    const currentMarkers = markers;
    currentMarkers.forEach(marker => {
      regions.addRegion({
        id: `marker-${marker.time}`,
        start: marker.time,
        end: marker.time + 0.1,
        color: 'rgba(255, 203, 5, 0.5)',
        drag: false,
        resize: false,
      });
    });
  }, [markers]);

  const addMarker = () => {
    if (!newMarkerLabel.trim()) return;

    const ws = wavesurferRef.current;
    const regions = regionsPluginRef.current;
    if (!ws || !regions) return;

    const time = currentTime;
    const label = newMarkerLabel.trim();

    // Add to markers state
    const newMarkers = [...markers, { time, label }];
    setMarkers(newMarkers);

    // Add visual marker region (thin line)
    if (!loopRegion) { // Only show if not in loop mode
      regions.addRegion({
        id: `marker-${time}`,
        start: time,
        end: time + 0.1,
        color: 'rgba(255, 203, 5, 0.5)',
        drag: false,
        resize: false,
      });
    }

    setNewMarkerLabel('');
    setShowAddMarker(false);
  };

  const deleteMarker = (markerTime) => {
    const regions = regionsPluginRef.current;
    if (regions) {
      const region = regions.getRegions().find(r => r.id === `marker-${markerTime}`);
      if (region) region.remove();
    }
    setMarkers(markers.filter(m => m.time !== markerTime));
  };

  const jumpToMarker = (time) => {
    const ws = wavesurferRef.current;
    if (!ws) return;
    ws.seekTo(time / duration);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="card-3d rounded-card p-4 border border-surface-border-light">
      {/* Waveform */}
      <div className="relative">
        <div ref={waveformRef} className="mb-4" />
        {!isReady && !loadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-card">
            <div className="text-center">
              <div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              <p className="text-xs text-text-subtle">Loading waveform...</p>
            </div>
          </div>
        )}
        {loadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-card">
            <div className="text-center text-red-400">
              <p className="text-sm mb-1">Failed to load audio</p>
              <p className="text-xs text-text-subtle">The audio file might be corrupted or unavailable</p>
            </div>
          </div>
        )}
      </div>

      {/* Time Display */}
      <div className="flex justify-between text-sm text-text-subtle mb-3">
        <span>{formatTime(currentTime)}</span>
        <span>{formatTime(duration)}</span>
      </div>

      {/* Main Controls */}
      <div className="flex items-center justify-center gap-3 mb-4">
        <button
          onClick={skipBackward}
          disabled={!isReady || loadError}
          className="bg-white/10 hover:bg-white/20 disabled:opacity-disabled disabled:cursor-not-allowed transition p-2 rounded-full"
          title="Skip back 5s"
        >
          <SkipBack className="w-4 h-4 text-white" />
        </button>

        <button
          onClick={togglePlayPause}
          disabled={!isReady || loadError}
          className="bg-brand hover:bg-brand-hover disabled:opacity-disabled disabled:cursor-not-allowed transition p-3 rounded-full"
          title="Play/Pause"
        >
          {isPlaying ? (
            <Pause className="w-5 h-5 text-white" />
          ) : (
            <Play className="w-5 h-5 text-white" />
          )}
        </button>

        <button
          onClick={skipForward}
          disabled={!isReady || loadError}
          className="bg-white/10 hover:bg-white/20 disabled:opacity-disabled disabled:cursor-not-allowed transition p-2 rounded-full"
          title="Skip forward 5s"
        >
          <SkipForward className="w-4 h-4 text-white" />
        </button>
      </div>

      {/* Advanced Controls */}
      <div className="flex flex-wrap items-center gap-3 mb-3">
        {/* Playback Speed */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Gauge className="w-4 h-4 text-text-subtle" />
            <span className="text-xs text-text-subtle">Speed:</span>
          </div>
          <div className="flex gap-1 flex-wrap">
            {[0.5, 0.75, 1.0, 1.25, 1.5, 2.0].map(rate => (
              <button
                key={rate}
                onClick={() => changePlaybackRate(rate)}
                disabled={!isReady || loadError}
                className={`px-2 py-1 text-xs rounded-card-btn transition ${
                  playbackRate === rate
                    ? 'bg-brand text-white'
                    : 'bg-white/10 text-text-muted hover:bg-white/20'
                } disabled:opacity-disabled disabled:cursor-not-allowed`}
              >
                {rate}x
              </button>
            ))}
          </div>
        </div>

        {/* A/B Loop */}
        {!isIOS && (
          <div className="flex items-center gap-2">
            {loopRegion ? (
              <button
                onClick={clearLoop}
                disabled={loadError}
                className="flex items-center gap-1 bg-brand hover:bg-brand-hover disabled:opacity-disabled disabled:cursor-not-allowed transition px-3 py-1 rounded-card-btn text-xs text-white whitespace-nowrap"
                title="Clear loop"
              >
                <Repeat className="w-3 h-3" />
                Loop Active
              </button>
            ) : (
              <button
                onClick={setABLoop}
                disabled={!isReady || loadError}
                className="flex items-center gap-1 bg-white/10 hover:bg-white/20 disabled:opacity-disabled disabled:cursor-not-allowed transition px-3 py-1 rounded-card-btn text-xs text-text-muted whitespace-nowrap"
                title="Set A/B loop from current position"
              >
                <Repeat className="w-3 h-3" />
                Set Loop
              </button>
            )}
          </div>
        )}

        {/* Add Marker */}
        {!isIOS && (
          <button
            onClick={() => setShowAddMarker(true)}
            disabled={!isReady || loadError}
            className="flex items-center gap-1 bg-white/10 hover:bg-white/20 disabled:opacity-disabled disabled:cursor-not-allowed transition px-3 py-1 rounded-card-btn text-xs text-text-muted whitespace-nowrap"
            title="Add marker"
          >
            <MapPin className="w-3 h-3" />
            Add Marker
          </button>
        )}
      </div>

      {/* Add Marker Input */}
      {!isIOS && showAddMarker && (
        <div className="mb-3 p-3 card-3d rounded-card border border-surface-border-light">
          <label className="block text-xs text-text-subtle mb-2">
            Marker at {formatTime(currentTime)}
          </label>
          <div className="flex flex-col gap-2">
            <input
              type="text"
              value={newMarkerLabel}
              onChange={(e) => setNewMarkerLabel(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') addMarker();
              }}
              placeholder="e.g., Verse 2, Solo starts..."
              className="w-full bg-transparent border border-surface-border-light px-4 py-2 text-sm text-white placeholder-text-subtle focus:outline-none focus:border-brand rounded-pill"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={addMarker}
                className="flex-1 bg-brand hover:bg-brand-hover transition px-3 py-2 rounded-pill text-xs text-white font-semibold"
              >
                Add
              </button>
              <button
                onClick={() => {
                  setShowAddMarker(false);
                  setNewMarkerLabel('');
                }}
                className="flex-1 bg-white/10 hover:bg-white/20 transition px-3 py-2 rounded-pill text-xs text-text-muted font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Markers List */}
      {!isIOS && markers.length > 0 && (
        <div className="border-t border-surface-border-light pt-3 mt-3">
          <p className="text-xs text-text-subtle mb-2">Markers:</p>
          <div className="space-y-2">
            {markers.sort((a, b) => a.time - b.time).map((marker, idx) => (
              <div
                key={idx}
                className="flex items-start gap-2 bg-white/10 rounded-card-btn px-3 py-2 hover:bg-white/20 transition"
              >
                <button
                  onClick={() => jumpToMarker(marker.time)}
                  className="flex-1 flex items-start gap-2 text-left min-w-0"
                >
                  <MapPin className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <span className="text-white text-sm block break-words">{marker.label}</span>
                    <span className="text-text-subtle text-xs block mt-0.5">{formatTime(marker.time)}</span>
                  </div>
                </button>
                <button
                  onClick={() => deleteMarker(marker.time)}
                  className="text-text-subtle hover:text-red-400 transition p-1 flex-shrink-0"
                  title="Delete marker"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
